/**
 * Vowella Gifts — "Gift Bag" cart add-on toggle + detail popup.
 * Pairs with snippets/vowella-gift-bag-addon.liquid.
 *
 * The gift bag is a REAL, separate Shopify product (its own price, its
 * own cart line) — never a line-item property — so its price is always
 * exactly what's set on that product in Admin, with zero risk of drifting
 * out of sync with a hardcoded number anywhere in this file.
 *
 * Behavior (Oct 2026):
 *   1. On page load, check the live cart (`${routes.cart_url}.js`) for a
 *      line matching this gift bag's variant ID. If found, the toggle
 *      reflects ON and is now "live" — flipping it acts on the cart
 *      immediately. If not found, the toggle just shows its configured
 *      default (data-default-on) as a PREFERENCE — no cart call yet.
 *   2. Clicking the toggle while it's not yet "live" (gift bag not in
 *      cart) only flips its visual state — nothing is charged until the
 *      shopper actually adds the main product to cart.
 *   3. Clicking the toggle while it IS live (gift bag already a cart
 *      line) calls the Cart AJAX API right away — POST cart_add_url to
 *      add it back, or cart_change_url with quantity 0 to remove it —
 *      and shows an inline "Gift Bag added" / "Gift Bag removed" status
 *      line, matching the client's own reference screenshot.
 *   4. Dawn's product-form.js (assets/product-form.js) publishes
 *      PUB_SUB_EVENTS.cartUpdate right after the main product is
 *      successfully added. This file subscribes to that same event: if
 *      the toggle is ON and the gift bag isn't a line yet, it rides
 *      along automatically with one extra /cart/add.js call — this is
 *      the "gắn kèm vô" (rides along attached) behavior the client asked
 *      for, so the shopper never has to click twice.
 *   5. Whichever path adds/removes it, the cart drawer / cart icon bubble
 *      are refreshed the exact same way Dawn's own product-form.js does
 *      (requesting `sections` on the same request, then handing the
 *      response to cart-drawer/cart-notification's own renderContents())
 *      — so the drawer never goes stale after a gift-bag-only change.
 *   6. Clicking the card's thumbnail or title name opens a <dialog> with
 *      a photo slideshow + description + its own "Add Gift Bag" button —
 *      clicking that button runs the exact same add-to-cart path as #3
 *      above, then closes the popup and updates the card's toggle/status
 *      to match. "No. I will prepare myself." and the X button just close
 *      the popup without doing anything to the cart.
 *
 * Every element this file needs lives inside one wrapper per card
 * (`[data-vowella-gift-bag]`, see the snippet) and is found with a plain
 * `wrapper.querySelector(...)` — deliberately not a DOM-order assumption
 * like "the next sibling" or a shared CSS counter, which is the exact
 * class of bug that bit the step-number badges elsewhere in this project
 * (see the brand/engineering notes, §21.4): fragile to reorder, easy to
 * silently break later. Keying everything off the wrapper avoids that.
 *
 * Relies on globals already loaded earlier in layout/theme.liquid
 * (constants.js, pubsub.js, global.js all load — and finish executing,
 * since every one of these is a plain deferred script in document order
 * — before this file does): `subscribe`/`publish`/`PUB_SUB_EVENTS` from
 * pubsub.js/constants.js, `window.routes` from the inline script in
 * theme.liquid. Same assumption Dawn's own product-form.js already makes.
 */
(function () {
  function getCartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function withSections(body, cartComponent) {
    if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
      var ids = cartComponent.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json();
    });
  }

  function findLine(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  function renderCartSections(cartComponent, response) {
    if (cartComponent && response && response.sections && typeof cartComponent.renderContents === 'function') {
      cartComponent.renderContents(response);
    }
  }

  function initSlideshow(wrapper) {
    var track = wrapper.querySelector('[data-vowella-gift-bag-track]');
    var dotsWrap = wrapper.querySelector('[data-vowella-gift-bag-dots]');
    if (!track) return { reset: function () {} };

    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-index]')) : [];
    var slideCount = track.children.length;

    function setActive(index) {
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === index);
      }
    }

    function scrollToIndex(index) {
      if (!track.clientWidth) return;
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      setActive(index);
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        scrollToIndex(index);
      });
    });

    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        if (!track.clientWidth || slideCount < 2) return;
        var index = Math.round(track.scrollLeft / track.clientWidth);
        setActive(index);
      }, 80);
    });

    return {
      reset: function () {
        track.scrollLeft = 0;
        setActive(0);
      },
    };
  }

  function initGiftBag(wrapper) {
    var variantId = wrapper.getAttribute('data-variant-id');
    var defaultOn = wrapper.getAttribute('data-default-on') === 'true';
    var toggle = wrapper.querySelector('[data-vowella-gift-bag-toggle]');
    var status = wrapper.querySelector('[data-vowella-gift-bag-status]');
    var dialog = wrapper.querySelector('[data-vowella-gift-bag-modal]');
    var openTriggers = wrapper.querySelectorAll('[data-vowella-gift-bag-open]');
    var modalAddBtn = dialog ? dialog.querySelector('[data-vowella-gift-bag-modal-add]') : null;
    var closeBtns = dialog ? dialog.querySelectorAll('[data-vowella-gift-bag-close]') : [];
    if (!variantId || !toggle) return;

    var slideshow = initSlideshow(wrapper);

    // Tracks whether this variant is a REAL cart line right now — only
    // once true does the toggle (or the popup's Add button) act on the
    // cart; before that it's a silent preference (see the file doc
    // comment above for why).
    var inCart = false;
    var busy = false;

    function setBusy(next) {
      busy = next;
      toggle.disabled = next;
      if (modalAddBtn) modalAddBtn.disabled = next || inCart;
    }

    function setStatus(text) {
      if (!status) return;
      status.hidden = !text;
      status.textContent = text || '';
    }

    function setChecked(checked) {
      toggle.setAttribute('aria-checked', checked ? 'true' : 'false');
    }

    function updateModalAddButton() {
      if (!modalAddBtn) return;
      if (inCart) {
        modalAddBtn.textContent = 'Added To Cart';
        modalAddBtn.disabled = true;
      } else {
        modalAddBtn.textContent = 'Add Gift Bag';
        modalAddBtn.disabled = busy;
      }
    }

    function openModal() {
      if (!dialog) return;
      slideshow.reset();
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeModal() {
      if (!dialog) return;
      if (typeof dialog.close === 'function') {
        if (dialog.open) dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    for (var i = 0; i < openTriggers.length; i++) {
      openTriggers[i].addEventListener('click', openModal);
    }
    for (var j = 0; j < closeBtns.length; j++) {
      closeBtns[j].addEventListener('click', closeModal);
    }
    if (dialog) {
      // Clicking the ::backdrop registers its click target as the dialog
      // element itself (nothing inside .vowella-gift-bag-modal__inner) —
      // the standard way to detect a backdrop click on a native <dialog>.
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) closeModal();
      });
    }

    fetch(window.routes.cart_url + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var line = findLine(cart, variantId);
        inCart = Boolean(line);
        setChecked(inCart || defaultOn);
        updateModalAddButton();
      })
      .catch(function () {
        // Cart fetch failed (offline, etc.) — fall back to the configured
        // default rather than leaving the switch in an unknown state.
        setChecked(defaultOn);
        updateModalAddButton();
      });

    function addGiftBag() {
      setBusy(true);
      var cartComponent = getCartComponent();
      return postJSON(window.routes.cart_add_url, withSections({ items: [{ id: variantId, quantity: 1 }] }, cartComponent))
        .then(function (response) {
          if (response.status) {
            setStatus(response.description || response.message || 'Could not add Gift Bag.');
            return;
          }
          inCart = true;
          setChecked(true);
          setStatus('Gift Bag added');
          renderCartSections(cartComponent, response);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vowella-gift-bag', cartData: response });
        })
        .catch(function () {
          setChecked(false);
          setStatus('Network error — please try again.');
        })
        .finally(function () {
          setBusy(false);
          updateModalAddButton();
        });
    }

    toggle.addEventListener('click', function () {
      if (busy) return;
      var turningOn = toggle.getAttribute('aria-checked') !== 'true';

      if (!inCart) {
        // Not a real cart line yet — this click just sets the preference
        // for whenever "Add to Cart" is pressed; nothing to call yet.
        setChecked(turningOn);
        return;
      }

      if (turningOn) {
        addGiftBag();
        return;
      }

      // Already in the cart and turning off: remove that line right now.
      setBusy(true);
      var cartComponent = getCartComponent();
      postJSON(window.routes.cart_change_url, withSections({ id: variantId, quantity: 0 }, cartComponent))
        .then(function (response) {
          if (response.status) {
            setChecked(true);
            setStatus(response.description || response.message || 'Could not remove Gift Bag.');
            return;
          }
          inCart = false;
          setChecked(false);
          setStatus('Gift Bag removed');
          renderCartSections(cartComponent, response);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vowella-gift-bag', cartData: response });
        })
        .catch(function () {
          setChecked(true);
          setStatus('Network error — please try again.');
        })
        .finally(function () {
          setBusy(false);
          updateModalAddButton();
        });
    });

    if (modalAddBtn) {
      modalAddBtn.addEventListener('click', function () {
        if (busy || inCart) return;
        addGiftBag().then(function () {
          if (inCart) closeModal();
        });
      });
    }

    // Ride along automatically once the main product is added elsewhere
    // on this page (Dawn's product-form.js publishes this right after a
    // successful /cart/add.js) — only if the toggle is on and we're not
    // already a line, and never react to our OWN add/remove above (that
    // would just re-add itself in a loop).
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (data && data.source === 'vowella-gift-bag') return;
      if (inCart || busy) return;
      if (toggle.getAttribute('aria-checked') !== 'true') return;
      addGiftBag();
    });
  }

  function boot() {
    var wrappers = document.querySelectorAll('[data-vowella-gift-bag]');
    for (var i = 0; i < wrappers.length; i++) {
      try {
        initGiftBag(wrappers[i]);
      } catch (err) {
        if (window.console) console.error('Vowella gift bag: init failed', err);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
